import {Alert, Choice} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {CurrencyFormatter, useFormatCurrency} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {Field} from 'react-final-form';

import {isNil, isNotNil} from 'ramda';

import {useGetAutomarketplEurotaxVariantsQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';

import type {AutomarketplEurotaxVariantsResponse} from '@prompt-bridge/shared/validations';

import {getSafely} from 'shared/web';

type Variant = AutomarketplEurotaxVariantsResponse['variants'][number];
type Dimension = NonNullable<AutomarketplEurotaxVariantsResponse['blockingDimension']>;

const DIMENSION_LABEL_KEYS: Partial<Record<Dimension, string>> = {
  make: 'entity.vehicle.labels.make',
  model: 'entity.vehicle.labels.modelFamily',
  registrationDate: 'entity.vehicle.labels.firstRegistration',
  fuel: 'entity.vehicle.labels.fuelType',
  hybrid: 'entity.vehicle.labels.fuelType',
  engineVolume: 'entity.vehicle.labels.cubicCapacity',
  power: 'entity.vehicle.labels.power',
  transmission: 'entity.vehicle.labels.transmission',
  bodyStyle: 'entity.vehicle.labels.carStyle',
  drive: 'entity.vehicle.labels.drive',
};

interface AutomarketplEurotaxSelectionProps {
  vehicleId: string;
  isDisabled: boolean;
}

function formatVariant(variant: Variant, formatCurrency: CurrencyFormatter) {
  return [
    variant.label,
    isNotNil(variant.powerKw) ? `${variant.powerKw} kW` : null,
    isNotNil(variant.engineVolume) ? `${variant.engineVolume} cm³` : null,
    variant.fuelType,
    variant.transmission,
    variant.validFrom?.slice(0, 4),
    isNotNil(variant.price) ? formatCurrency(variant.price, 'PLN') : null,
  ]
    .filter(isNotNil)
    .join(' · ');
}

function getMissingText(data: AutomarketplEurotaxVariantsResponse) {
  const labelKey = isNil(data.blockingDimension)
    ? undefined
    : getSafely(DIMENSION_LABEL_KEYS, data.blockingDimension);
  if (isNil(labelKey)) {
    return i18n.t('page.vehicleDetail.automarketplEurotax.missing');
  }
  const reason =
    data.reasonCode === 'input_dimension_missing'
      ? i18n.t('page.vehicleDetail.automarketplEurotax.missingField')
      : i18n.t('page.vehicleDetail.automarketplEurotax.noMatchingVariant');
  return `${reason}: ${i18n.t(labelKey)}`;
}

function getStatus(data: AutomarketplEurotaxVariantsResponse, selectedLabel: string | undefined) {
  return match({data, selectedLabel})
    .with({data: {reasonCode: 'manual_selection_invalid'}}, () => ({
      variant: 'error' as const,
      text: i18n.t('page.vehicleDetail.automarketplEurotax.invalid'),
    }))
    .with({data: {result: 'ambiguous'}}, () => ({
      variant: 'warning' as const,
      text: i18n.t('page.vehicleDetail.automarketplEurotax.ambiguous', {
        count: data.variants.length,
      }),
    }))
    .with({data: {result: 'missing'}}, () => ({
      variant: 'error' as const,
      text: getMissingText(data),
    }))
    .with(
      {data: {reasonCode: Pattern.not('manual_selection')}, selectedLabel: Pattern.string},
      ({selectedLabel: label}) => ({
        variant: 'info' as const,
        text: i18n.t('page.vehicleDetail.automarketplEurotax.automatic', {variant: label}),
      })
    )
    .otherwise(() => null);
}

export function AutomarketplEurotaxSelection(props: AutomarketplEurotaxSelectionProps) {
  const formatCurrency = useFormatCurrency();
  const {data, isLoading, isError} = useGetAutomarketplEurotaxVariantsQuery({
    vehicleId: props.vehicleId,
  });

  if (isError) {
    return (
      <Alert
        type="inline"
        variant="warning"
        title={i18n.t('entity.workshop.labels.notAvailable')}
      />
    );
  }

  const options = (data?.variants ?? []).map((variant) => ({
    value: variant.eurotaxCode,
    label: formatVariant(variant, formatCurrency),
  }));
  const selectedLabel = options.find((option) => option.value === data?.selectedEurotaxCode)?.label;
  const status = data ? getStatus(data, selectedLabel) : null;

  return (
    <Field<number | undefined> name="platformInput.kodEurotax">
      {({input}) => (
        <VStack spacing={2}>
          <Choice<number>
            label={i18n.t('page.vehicleDetail.automarketplEurotax.label')}
            placeholder={i18n.t('page.vehicleDetail.automarketplEurotax.automaticOption')}
            value={input.value ?? null}
            options={options}
            onChange={(value) => input.onChange(value ?? undefined)}
            isLoading={isLoading}
            isDisabled={props.isDisabled || isLoading}
            isSearchable
            menuInPortal
          />
          <Show when={status}>
            {(currentStatus) => (
              <Alert type="inline" variant={currentStatus.variant} message={currentStatus.text} />
            )}
          </Show>
        </VStack>
      )}
    </Field>
  );
}
